/** Complete a proof-bound OAuth browser transaction in a Next.js Route Handler. */
import {
  AuthClient,
  type OAuthResult,
  type OAuthTransactionStorage,
  parseOAuthConfig,
} from '@palbase/auth';
import { HttpClient, TokenManager } from '@palbase/core';
import type { NextRequest } from 'next/server';
import { environmentRefFromPublishableApiKey } from '../api-key.js';
import { encodeSessionCookiesDecoded, SESSION_COOKIE_ATTRS } from './cookie-codec.js';
import { requireGlobalConfig } from './global-config.js';
import { oauthCookieStorage } from './oauth-storage.js';
import { importNextServer } from './shared.js';

export interface HandleAuthCallbackOptions {
  /** Same-origin destination after success or error. Default '/'. */
  defaultNext?: string;
  /** Render or hand off the MFA challenge. By default it is returned as uncached JSON. */
  onMFARequired?: (
    result: Extract<OAuthResult, { status: 'mfaRequired' }>,
    request: NextRequest,
  ) => Response | Promise<Response>;
}

function safeNextPath(next: string | null, origin: string): string | null {
  if (!next) return null;
  // 1a. No control chars / whitespace — URL parsing would strip these and
  //     change what the path means (the \t \n \r open-redirect class).
  // biome-ignore lint/suspicious/noControlCharactersInRegex: rejecting C0 is the point of the guard.
  if (/[\u0000-\u0020\u007f\s]/.test(next)) return null;
  // 1b. Must be path-absolute: a single leading '/', not '//' or '/\\'.
  if (next[0] !== '/' || next[1] === '/' || next[1] === '\\') return null;
  // 1c. No scheme before the first path separator (defense in depth; a
  //     path-absolute string can't carry one, but reject explicitly).
  const firstSlash = next.indexOf('/');
  const firstColon = next.indexOf(':');
  if (firstColon !== -1 && firstColon < firstSlash) return null;
  // 2. Canonical same-origin check — resolve as the browser/redirect helper
  //    will and confirm we never leave this origin.
  try {
    if (new URL(next, origin).origin !== origin) return null;
  } catch {
    return null;
  }
  return next;
}

export function handleAuthCallback(
  opts?: HandleAuthCallbackOptions,
): (request: NextRequest) => Promise<Response> {
  return async (request) => {
    const { NextResponse } = await importNextServer('handleAuthCallback');
    const fallback = safeNextPath(opts?.defaultNext ?? '/', request.nextUrl.origin) ?? '/';
    const writes = new Map<string, { value: string; options?: Record<string, unknown> }>();
    const finish = (response: InstanceType<typeof NextResponse>) => {
      for (const [name, write] of writes) response.cookies.set(name, write.value, write.options);
      response.headers.set('cache-control', 'private, no-store');
      response.headers.set('referrer-policy', 'no-referrer');
      return response;
    };
    const redirect = (error?: string) => {
      const url = new URL(fallback, request.nextUrl);
      if (error) url.searchParams.set('auth_error', error);
      return finish(NextResponse.redirect(url));
    };
    const params = request.nextUrl.searchParams;
    const transactionId = params.get('transaction_id');
    const resultCode = params.get('result_code');
    if (
      !transactionId ||
      !resultCode ||
      params.size !== 2 ||
      params.getAll('transaction_id').length !== 1 ||
      params.getAll('result_code').length !== 1
    )
      return redirect('invalid_callback');

    const config = requireGlobalConfig(
      'Import the generated palbe.gen.ts and start OAuth with pbServer() in a Server Action.',
    );
    const ref = environmentRefFromPublishableApiKey(config.apiKey);
    const storage: OAuthTransactionStorage = oauthCookieStorage({
      get(name) {
        const write = writes.get(name);
        return write
          ? write.value
            ? { name, value: write.value }
            : undefined
          : request.cookies.get(name);
      },
      set(name, value, options) {
        writes.set(name, { value, options });
      },
    });
    const http = new HttpClient(config.apiKey, {
      url: config.url,
      sealed: config.sealed,
      headers: config.headers,
    });
    const auth = new AuthClient(http, new TokenManager(), config.apiKey, config.url, {
      oauth: config.oauth === undefined ? undefined : parseOAuthConfig(config.oauth, ref),
      oauthStorage: storage,
    });
    const exchange = await auth.completeOAuth({
      transactionId,
      resultCode,
      callbackURL: request.nextUrl.href,
    });
    if (exchange.error) return redirect(exchange.error.code);
    const result = exchange.data;
    if (!result) return redirect('invalid_oauth_contract');
    if (result.status === 'mfaRequired') {
      if (opts?.onMFARequired) {
        const custom = await opts.onMFARequired(result, request);
        return finish(
          new NextResponse(custom.body, {
            status: custom.status,
            statusText: custom.statusText,
            headers: custom.headers,
          }),
        );
      }
      return finish(NextResponse.json(result, { status: 202 }));
    }
    if (result.status === 'linked') return redirect();
    const response = NextResponse.redirect(new URL(fallback, request.nextUrl));
    const write = encodeSessionCookiesDecoded(ref, result.session);
    for (const { name, value } of write.set)
      response.cookies.set(name, value, SESSION_COOKIE_ATTRS);
    for (const name of write.clear)
      response.cookies.set(name, '', { ...SESSION_COOKIE_ATTRS, maxAge: 0 });
    return finish(response);
  };
}
